/**
 * RTNotesBlock — Consultor's notepad during the Reunião Técnica.
 *
 * Flow:
 *   1) Consultor types free-form notes (with quick "textos automáticos" chips).
 *   2) Clicks "Melhorar com IA" → Claude Sonnet splits the text into
 *      a client-facing narrative + strictly-internal observations, and
 *      extracts structured suggestions for the care-plan fields.
 *   3) Consultor picks which suggestions to apply → one-click update
 *      of ``technical.clinical_state``.
 *
 * Visibility: only rendered when the lead is in the Consultor or later
 * phase (owner.phase ≠ "atendimento"). This avoids clutter for the SAC
 * and matches the sequential workflow (Atendimento → Consultor → …).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Save, Wand2, ClipboardCopy, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronRight, User, Lock, FileText, X,
} from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// Chip button used both for category headers and for text-inserting
// snippets. Uses a subtle warm-bronze palette matching the RT context.
function Chip({ selected, onClick, children, dim, testid, title }) {
  const base = "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors cursor-pointer whitespace-nowrap";
  const style = selected
    ? "bg-foreground text-background border-[#0F172A]"
    : dim
      ? "bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
      : "bg-card text-foreground border-border hover:border-[#B5895A] hover:bg-muted/40";
  return (
    <button type="button" onClick={onClick} className={`${base} ${style}`} data-testid={testid} title={title || ""}>
      {children}
    </button>
  );
}


export default function RTNotesBlock({ lead, currentUser, ownerPhase, onCarePlanChanged }) {
  // Only render when the lead is past the Atendimento phase AND the
  // current user has a role that can operate the notepad. SAC sees the
  // lead itself but not this block — it belongs to the Consultor's
  // workflow. Admins/gestores keep access for review/continuation.
  const role = (currentUser?.role || "").toLowerCase();
  const gated = ownerPhase === "atendimento" || !["consultor", "admin", "gestor"].includes(role);

  const [rawText, setRawText] = useState("");
  const [snippets, setSnippets] = useState([]);
  const [catLabels, setCatLabels] = useState({});
  const [carePlanFields, setCarePlanFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [ai, setAi] = useState(null);
  // The exact ``raw_text`` that was fed to the LLM the last time
  // enhance() ran. Used to compute an accurate ``staleAi`` cue —
  // dirty alone is unreliable because the consultor can save without
  // re-running the AI, which resets dirty but leaves the AI panel
  // pointing at outdated content.
  const [aiSourceText, setAiSourceText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [pickField, setPickField] = useState({}); // {field: bool}
  const [expandedCat, setExpandedCat] = useState({});
  // 20/02/2026 — Apply-to-Zoho / Apply-to-Proposal states
  const [applyingInternal, setApplyingInternal] = useState(false);
  const [applyingClient, setApplyingClient] = useState(false);
  const [applyingBoth, setApplyingBoth] = useState(false);
  // 20/02/2026 — Post-enhance auto-apply prompt: after the AI splits
  // into client/internal, ask the Consultor whether to push the
  // client-facing text into the proposal right away (saves a scroll
  // + click). Modal only opens once per enhance run.
  const [autoApplyOpen, setAutoApplyOpen] = useState(false);
  // 20/02/2026 — Overwrite confirmation before pushing the client text
  // into ``proposal.additional_notes`` when it already has content.
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  // Autosave draft state — the timestamp of the last localStorage
  // flush drives the tiny "Guardado às HH:MM" indicator under the
  // textareas. Draft is cleared on successful apply / save.
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const _draftKey = lead?.id ? `caring_rt_draft_${lead.id}` : null;
  const textareaRef = useRef(null);

  // Hydrate notepad + snippet catalog on mount / lead change
  useEffect(() => {
    if (gated || !lead?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch in parallel but don't fail-fast — the snippet catalog
        // is public to auth'd users while the notepad is scoped; a
        // 403 on notes must not blank the snippet picker.
        const [notesR, snipR] = await Promise.allSettled([
          api.get(`/leads/${lead.id}/rt-note`),
          api.get("/rt-note-snippets?active_only=true"),
        ]);
        if (cancelled) return;
        if (notesR.status === "fulfilled") {
          const rt = notesR.value.data?.raw_text || "";
          setRawText(rt);
          setAi(notesR.value.data?.ai_result || null);
          // Assume the persisted AI result matches the persisted raw
          // text on load — the backend snapshots them together.
          setAiSourceText(rt);

          // 20/02/2026 — Draft restore. If a local draft is newer
          // than the server payload (or the server has nothing yet),
          // we resurrect it so the Consultor never loses work after a
          // browser refresh/crash.
          try {
            const raw = _draftKey && localStorage.getItem(_draftKey);
            if (raw) {
              const draft = JSON.parse(raw);
              if (draft && (draft.raw_text || draft.client_text || draft.internal_text)) {
                // Only merge non-empty fields; don't override server
                // truth with an empty draft.
                if (draft.raw_text && draft.raw_text !== rt) setRawText(draft.raw_text);
                if (draft.client_text || draft.internal_text) {
                  setAi((prev) => ({
                    ...(prev || {}),
                    client_text: draft.client_text ?? prev?.client_text ?? "",
                    internal_text: draft.internal_text ?? prev?.internal_text ?? "",
                  }));
                }
                if (draft.saved_at) setDraftSavedAt(draft.saved_at);
                toast.info("Rascunho local recuperado");
              }
            }
          } catch { /* ignore corrupt draft */ }
        }
        if (snipR.status === "fulfilled") {
          setSnippets(snipR.value.data?.snippets || []);
          setCatLabels(snipR.value.data?.category_labels || {});
          setCarePlanFields(snipR.value.data?.care_plan_fields || []);
        }
        setDirty(false);
      } catch (e) {
        toast.error("Erro a carregar notas de RT");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead?.id, gated]);

  // Group snippets by category for the sidebar picker
  const snippetsByCategory = useMemo(() => {
    const out = {};
    for (const s of snippets) (out[s.category] ||= []).push(s);
    return out;
  }, [snippets]);

  // 20/02/2026 — "Last applied" indicator. Scans ``lead.notes`` for
  // the most recent ``kind=ai_proposal`` entry — that's the one the
  // Consultor pushed to the proposal via "Aplicar cliente → Proposta"
  // or the auto-apply modal. Surfaces the author + relative time in
  // the block header so the Gestor sees at a glance whether the RT
  // has been closed.
  const lastAppliedProposal = useMemo(() => {
    const list = (lead?.notes || []).filter((n) => n.kind === "ai_proposal" && n.created_at);
    if (!list.length) return null;
    return list.slice().sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  }, [lead?.notes]);

  const _relativeTime = (iso) => {
    if (!iso) return "";
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const mins = Math.round(diff / 60000);
      if (mins < 1) return "agora mesmo";
      if (mins < 60) return `há ${mins} min`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `há ${hrs} h`;
      const days = Math.round(hrs / 24);
      if (days < 30) return `há ${days} dia${days === 1 ? "" : "s"}`;
      return new Date(iso).toLocaleDateString("pt-PT");
    } catch { return ""; }
  };

  // 20/02/2026 — Autosave draft every 5s. Persists the raw notes and
  // both editable AI panels (client/internal) to localStorage, keyed
  // by lead id, so a browser crash / accidental refresh doesn't wipe
  // the Consultor's work-in-progress. Cleared explicitly on
  // successful apply-to-Zoho / apply-to-Proposal (see below).
  useEffect(() => {
    if (gated || !_draftKey) return;
    const iv = setInterval(() => {
      const payload = {
        raw_text: rawText || "",
        client_text: (ai?.client_text || ""),
        internal_text: (ai?.internal_text || ""),
        saved_at: new Date().toISOString(),
      };
      // Skip persistence when everything is empty (avoids stamping an
      // "empty draft" flag on fresh leads).
      if (!payload.raw_text && !payload.client_text && !payload.internal_text) return;
      try {
        localStorage.setItem(_draftKey, JSON.stringify(payload));
        setDraftSavedAt(payload.saved_at);
      } catch { /* quota exceeded — ignore */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [gated, _draftKey, rawText, ai?.client_text, ai?.internal_text]);

  const _clearDraft = () => {
    try { if (_draftKey) localStorage.removeItem(_draftKey); } catch {}
    setDraftSavedAt(null);
  };

  if (gated) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/leads/${lead.id}/rt-note`, { raw_text: rawText });
      setAi(r.data?.ai_result || null);
      setDirty(false);
      toast.success("Notas gravadas");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a gravar");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // 20/02/2026 — Apply the (editable) internal text to the lead's
  // notes stream. The regular ``/leads/{id}/notes`` endpoint stamps
  // ``author_role`` server-side and mirrors the entry to Zoho CRM, so
  // the internal text lives on the Zoho lead card next to the
  // Atendimento notes.
  const applyInternalToZoho = async () => {
    const text = (ai?.internal_text || "").trim();
    if (!text) { toast.error("Texto interno vazio"); return; }
    setApplyingInternal(true);
    try {
      await api.post(`/leads/${lead.id}/notes`, { content: text });
      toast.success("Nota interna gravada e enviada ao Zoho");
      // Draft was successfully persisted server-side → drop the local
      // copy. Client draft may still be pending so we keep the
      // client_text side intact.
      if (_draftKey) {
        try {
          const cur = JSON.parse(localStorage.getItem(_draftKey) || "{}");
          const next = { ...cur, internal_text: "" };
          if (!next.raw_text && !next.client_text) localStorage.removeItem(_draftKey);
          else localStorage.setItem(_draftKey, JSON.stringify(next));
        } catch {}
      }
      onCarePlanChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar interno");
    } finally {
      setApplyingInternal(false);
    }
  };

  // Apply the (editable) client text to ``proposal.additional_notes``
  // — it surfaces on the proposal PDF and public page, attributed to
  // the current Consultor. If the proposal already carries notes we
  // open a confirmation modal so the Consultor doesn't nuke previous
  // content by accident.
  const _confirmApplyClient = () => {
    const text = (ai?.client_text || "").trim();
    if (!text) { toast.error("Texto para o cliente vazio"); return; }
    // Autoclose the auto-apply prompt if it triggered this call.
    const existing = ((lead?.proposal || {}).additional_notes || "").trim();
    if (existing && existing !== text) {
      setOverwriteOpen(true);
      return;
    }
    applyClientToProposal();
  };

  const applyClientToProposal = async () => {
    const text = (ai?.client_text || "").trim();
    if (!text) { toast.error("Texto para o cliente vazio"); return; }
    setApplyingClient(true);
    try {
      await api.post(`/leads/${lead.id}/notes/ai-apply`, {
        target: "proposal",
        content: text,
      });
      toast.success("Texto aplicado à proposta");
      if (_draftKey) {
        try {
          const cur = JSON.parse(localStorage.getItem(_draftKey) || "{}");
          const next = { ...cur, client_text: "" };
          if (!next.raw_text && !next.internal_text) localStorage.removeItem(_draftKey);
          else localStorage.setItem(_draftKey, JSON.stringify(next));
        } catch {}
      }
      onCarePlanChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar à proposta");
    } finally {
      setApplyingClient(false);
    }
  };

  const insertSnippet = (snippet) => {
    const el = textareaRef.current;
    // ``selectionStart`` can legitimately be 0 (caret at the very start);
    // using ``|| rawText.length`` would silently push the insertion to
    // the end. Use ``??`` so only null/undefined fall back to end.
    const start = el?.selectionStart ?? rawText.length;
    const end = el?.selectionEnd ?? rawText.length;
    const before = rawText.slice(0, start);
    const after = rawText.slice(end);
    const needsSpace = before.length > 0 && !/[\s\n]$/.test(before);
    const insertion = (needsSpace ? " " : "") + snippet.text;
    const next = before + insertion + after;
    setRawText(next);
    setDirty(true);
    // Restore caret to the end of the inserted text.
    const caret = before.length + insertion.length;
    setTimeout(() => {
      if (el) {
        el.focus();
        try { el.setSelectionRange(caret, caret); } catch { /* noop */ }
      }
    }, 10);
  };

  const enhance = async () => {
    // Guard: the button is already disabled when text is empty, but
    // this keeps the handler safe if called programmatically.
    if (!rawText.trim()) {
      toast.error("Escreva algo antes de pedir IA.");
      return;
    }
    if (dirty) {
      toast.info("A gravar antes de correr a IA…");
      const ok = await save();
      if (!ok) return;  // save failed → don't run stale IA
    }
    setEnhancing(true);
    try {
      const r = await api.post(`/leads/${lead.id}/rt-note/enhance`);
      setAi(r.data);
      setAiSourceText(rawText);
      // Pre-select all non-empty suggestions so the consultor can just
      // review + click "Aplicar" without ticking each box manually.
      const picks = {};
      for (const f of carePlanFields) {
        const val = (r.data?.care_plan_suggestions || {})[f];
        if (val && val.trim()) picks[f] = true;
      }
      setPickField(picks);
      toast.success("IA processou as notas");
      // 20/02/2026 — Prompt the Consultor to auto-apply the client
      // text to the proposal. Only offer it when the AI actually
      // produced client-facing text and a proposal already exists.
      const clientText = (r.data?.client_text || "").trim();
      if (clientText && lead?.proposal) {
        setAutoApplyOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "A IA falhou — tente de novo");
    } finally {
      setEnhancing(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success(`${label} copiado`); }
    catch { toast.error("Não consegui copiar"); }
  };

  const applyToCarePlan = async () => {
    const fields = Object.entries(pickField).filter(([_, v]) => v).map(([k]) => k);
    if (!fields.length) { toast.error("Escolha pelo menos um campo"); return; }
    setApplying(true);
    try {
      const r = await api.post(`/leads/${lead.id}/rt-note/apply-to-care-plan`, { fields });
      toast.success(`Aplicado a ${Object.keys(r.data?.applied || {}).length} campos do plano`);
      onCarePlanChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a aplicar");
    } finally {
      setApplying(false);
    }
  };

  // Stale = the current text differs from what the AI last processed.
  // This is a much more reliable signal than ``dirty`` because it also
  // catches "consultor saved without re-running IA" — the panel now
  // properly warns the reader that the AI output is out of date.
  const staleAi = ai && aiSourceText && rawText.trim() !== aiSourceText.trim();

  return (
    <div
      data-testid="rt-notes-block"
      className="rounded-2xl border border-[#B5895A]/40 bg-gradient-to-br from-card to-[hsl(var(--muted))] p-5 lg:p-6 shadow-sm"
    >
      <header
        className="flex items-start justify-between gap-3 cursor-pointer"
        onClick={() => setCollapsed(v => !v)}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-foreground flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#B5895A]">
              Reunião Técnica · Bloco de notas
            </div>
            <h3 className="font-ui text-lg text-foreground">
              Notas do Consultor{" "}
              {ai && <span className="text-xs text-[hsl(var(--success))] ml-1">· IA processada</span>}
            </h3>
            {lastAppliedProposal && (
              <div
                data-testid="rt-notes-last-applied"
                className="mt-1 inline-flex items-center gap-1.5 text-[11px] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] rounded-full px-2.5 py-0.5"
                title={`Aplicado a ${new Date(lastAppliedProposal.created_at).toLocaleString("pt-PT")}`}
              >
                <CheckCircle2 className="w-3 h-3" />
                Última nota aplicada · <b className="font-semibold">{(lastAppliedProposal.author_name || "").replace(/ \(via IA\)$/i, "")}</b> · {_relativeTime(lastAppliedProposal.created_at)}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              Escreva a minuta da RT. Use "textos rápidos" para acelerar. No fim,
              carregue em "Melhorar com IA" para separar o que é para o cliente,
              o que é interno, e pré-preencher o plano de cuidados.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="rt-notes-toggle"
          className="text-muted-foreground hover:text-foreground flex-shrink-0"
          aria-label={collapsed ? "Expandir" : "Colapsar"}
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </header>

      {!collapsed && (
        <div className="mt-4">
          {loading ? (
            <div className="text-sm text-muted-foreground italic py-6 text-center">A carregar…</div>
          ) : (
            <div>
              {/* 20/02/2026 — Quick note to Zoho: skip the whole
                  minute + AI dance for one-liners ("confirmado o
                  horário", "cliente pediu para chamar amanhã"). Fires
                  the standard ``/leads/{id}/notes`` endpoint that
                  stamps ``author_role`` and mirrors to Zoho. */}
              <QuickZohoNote
                leadId={lead.id}
                onSaved={onCarePlanChanged}
              />
              <div className="grid lg:grid-cols-[minmax(0,1fr)_280px] gap-4">
              {/* Left column — textarea */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                    Minuta em bruto
                  </label>
                  <div className="text-[10px] text-muted-foreground">
                    {rawText.length} car. · {dirty ? "não guardado" : "guardado"}
                    {draftSavedAt && (
                      <span
                        data-testid="rt-notes-draft-indicator"
                        className="ml-2 inline-flex items-center gap-1 text-[hsl(var(--success))]"
                        title={`Rascunho local: ${new Date(draftSavedAt).toLocaleString("pt-PT")}`}
                      >
                        · rascunho ✓ {new Date(draftSavedAt).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                  </div>
                </div>
                <textarea
                  ref={textareaRef}
                  data-testid="rt-notes-textarea"
                  className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-[#1D4ED8]/30 font-mono"
                  rows={12}
                  value={rawText}
                  onChange={(e) => { setRawText(e.target.value); setDirty(true); }}
                  placeholder="Ex: Beneficiário 82 anos, Alzheimer moderado, filha exausta, anda com andarilho, tem caído..."
                  disabled={saving || enhancing}
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    data-testid="rt-notes-save"
                    onClick={save}
                    disabled={!dirty || saving || enhancing}
                    className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 font-semibold disabled:opacity-40"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Gravar
                  </button>
                  <button
                    data-testid="rt-notes-enhance"
                    onClick={enhance}
                    disabled={enhancing || saving || !rawText.trim()}
                    className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40"
                  >
                    {enhancing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Melhorar com IA
                  </button>
                </div>
              </div>

              {/* Right sidebar — snippet picker */}
              <aside className="bg-card/60 border border-border rounded-lg p-3 max-h-[420px] overflow-y-auto">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">
                  Textos rápidos
                </div>
                {Object.keys(snippetsByCategory).length === 0 && (
                  <div className="text-xs text-muted-foreground italic">Sem snippets carregados.</div>
                )}
                <div className="space-y-2">
                  {Object.keys(snippetsByCategory).sort().map((cat) => (
                    <div key={cat} data-testid={`rt-snippet-cat-${cat}`}>
                      <button
                        type="button"
                        onClick={() => setExpandedCat(m => ({ ...m, [cat]: !m[cat] }))}
                        className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#B5895A] hover:text-[#8b6a41] flex items-center gap-1 w-full text-left"
                      >
                        {expandedCat[cat] !== false ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {catLabels[cat] || cat}
                      </button>
                      {expandedCat[cat] !== false && (
                        <div className="flex flex-wrap gap-1 mt-1 mb-2 ml-3">
                          {snippetsByCategory[cat].map((s) => (
                            <Chip
                              key={s.id}
                              onClick={() => insertSnippet(s)}
                              testid={`rt-snippet-${s.id}`}
                              title={s.text}
                            >
                              {s.label}
                            </Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            </div>
            </div>
          )}

          {/* AI Result panel */}
          {ai && !loading && (
            <div className="mt-6 pt-6 border-t border-border" data-testid="rt-notes-ai-result">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--success))] font-semibold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> IA processou {ai.processed_at ? "em " + new Date(ai.processed_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                  <h4 className="font-ui text-base text-foreground mt-0.5">
                    Resultado — divisão texto cliente / interno + sugestões plano de cuidados
                  </h4>
                </div>
                {staleAi && (
                  <div className="text-[11px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-md px-2 py-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Notas alteradas depois da última execução
                  </div>
                )}
              </div>

              {/* Client vs Internal side-by-side. Both panels are
                  editable so the Consultor can tweak the AI output
                  before pushing. Two dedicated apply buttons persist
                  the results: internal → Zoho (as a note), client →
                  ``proposal.additional_notes`` (surfaces on the PDF
                  and public page). */}
              <div className="grid md:grid-cols-2 gap-3">
                <div
                  data-testid="rt-notes-client-panel"
                  className="rounded-lg border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/50 p-3"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--success))] flex items-center gap-1">
                      <User className="w-3 h-3" /> Para o cliente
                    </div>
                    <button
                      data-testid="rt-notes-copy-client"
                      onClick={() => copyToClipboard(ai.client_text, "Texto cliente")}
                      className="text-[hsl(var(--success))] hover:text-[hsl(var(--success))]"
                      title="Copiar"
                    >
                      <ClipboardCopy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <textarea
                    data-testid="rt-notes-client-textarea"
                    value={ai.client_text || ""}
                    onChange={(e) => setAi((prev) => ({ ...prev, client_text: e.target.value }))}
                    rows={7}
                    placeholder="— vazio —"
                    className="w-full bg-card border border-[hsl(var(--success))]/25 rounded-md px-2.5 py-2 text-sm text-foreground focus:outline-none focus:border-emerald-500 resize-y min-h-[130px] leading-relaxed"
                  />
                  <button
                    type="button"
                    data-testid="rt-notes-apply-client"
                    onClick={_confirmApplyClient}
                    disabled={applyingClient || !(ai.client_text || "").trim()}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                    title="Grava o texto do cliente no bloco 'Notas do Consultor' da proposta (visível no PDF e página pública)"
                  >
                    {applyingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Aplicar cliente → Proposta
                  </button>
                </div>

                <div
                  data-testid="rt-notes-internal-panel"
                  className="rounded-lg border border-border bg-muted/40 p-3"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Só interno
                    </div>
                    <button
                      data-testid="rt-notes-copy-internal"
                      onClick={() => copyToClipboard(ai.internal_text, "Texto interno")}
                      className="text-muted-foreground hover:text-foreground"
                      title="Copiar"
                    >
                      <ClipboardCopy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <textarea
                    data-testid="rt-notes-internal-textarea"
                    value={ai.internal_text || ""}
                    onChange={(e) => setAi((prev) => ({ ...prev, internal_text: e.target.value }))}
                    rows={7}
                    placeholder="— sem observações internas —"
                    className="w-full bg-card border border-border rounded-md px-2.5 py-2 text-sm text-foreground focus:outline-none focus:border-slate-500 resize-y min-h-[130px] leading-relaxed"
                  />
                  <button
                    type="button"
                    data-testid="rt-notes-apply-internal"
                    onClick={applyInternalToZoho}
                    disabled={applyingInternal || !(ai.internal_text || "").trim()}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-foreground hover:bg-foreground/90 text-background rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                    title="Cria uma nota interna no lead e sincroniza-a com o Zoho CRM"
                  >
                    {applyingInternal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                    Guardar interno → Zoho
                  </button>
                </div>
              </div>

              {/* Corrections */}
              {(ai.corrections || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] p-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--warning))] flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3 h-3" /> Sugestões / lacunas
                  </div>
                  <ul className="text-xs text-[hsl(var(--warning))] list-disc list-inside space-y-1">
                    {ai.corrections.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}

              {/* Care plan suggestions */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
                    Sugestões para plano de cuidados
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="rt-notes-toggle-all"
                      onClick={() => {
                        const anySelected = Object.values(pickField).some(Boolean);
                        const next = {};
                        if (!anySelected) {
                          for (const f of carePlanFields) {
                            const v = ai.care_plan_suggestions?.[f];
                            if (v && v.trim()) next[f] = true;
                          }
                        }
                        setPickField(next);
                      }}
                      className="text-[11px] text-muted-foreground hover:text-foreground underline"
                    >
                      {Object.values(pickField).some(Boolean) ? "Desmarcar todos" : "Marcar todos"}
                    </button>
                    <button
                      data-testid="rt-notes-apply"
                      onClick={applyToCarePlan}
                      disabled={applying || !Object.values(pickField).some(Boolean)}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                    >
                      {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Aplicar ao plano de cuidados
                    </button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2" data-testid="rt-notes-suggestions">
                  {carePlanFields.map((f) => {
                    const val = (ai.care_plan_suggestions || {})[f] || "";
                    if (!val.trim()) return null;
                    const label = catLabels[f] || f;
                    return (
                      <label
                        key={f}
                        data-testid={`rt-notes-suggest-${f}`}
                        className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer text-sm transition-colors ${
                          pickField[f] ? "border-emerald-400 bg-[hsl(var(--success-bg))]/40" : "border-border bg-card hover:border-border"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={!!pickField[f]}
                          onChange={(e) => setPickField(m => ({ ...m, [f]: e.target.checked }))}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
                            {label}
                          </div>
                          <div className="text-foreground leading-snug">{val}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 20/02/2026 — Auto-apply prompt (opens right after "Melhorar com
          IA"). Shows a short preview of the client-facing text and
          lets the Consultor push it into ``proposal.additional_notes``
          in a single click, without scrolling to the panels below. */}
      <Dialog open={autoApplyOpen} onOpenChange={setAutoApplyOpen}>
        <DialogContent data-testid="rt-notes-autoapply-modal" className="max-w-lg bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-lg inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[hsl(var(--success))]" />
              Aplicar já à proposta?
            </DialogTitle>
            <DialogDescription>
              A IA gerou um texto para o cliente. Quer aplicá-lo já ao bloco
              "Notas do Consultor" da proposta ou prefere rever antes?
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/50 p-3 text-sm text-foreground max-h-[220px] overflow-y-auto whitespace-pre-wrap leading-relaxed">
            {(ai?.client_text || "").trim() || <i className="text-muted-foreground">Sem texto para o cliente.</i>}
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setAutoApplyOpen(false)}
              data-testid="rt-notes-autoapply-later"
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold"
            >
              <X className="w-3.5 h-3.5" /> Rever primeiro
            </button>
            <button
              type="button"
              disabled={applyingClient || !(ai?.client_text || "").trim()}
              onClick={async () => {
                setAutoApplyOpen(false);
                _confirmApplyClient();
              }}
              data-testid="rt-notes-autoapply-now"
              className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-40"
            >
              {applyingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Aplicar agora à proposta
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 20/02/2026 — Overwrite confirmation. Fires when the Consultor
          hits "Aplicar cliente → Proposta" AND
          ``proposal.additional_notes`` already carries content — the
          field is not appended, it is overwritten, so we make the
          consequence explicit. */}
      <Dialog open={overwriteOpen} onOpenChange={setOverwriteOpen}>
        <DialogContent data-testid="rt-notes-overwrite-modal" className="max-w-lg bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-lg inline-flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[hsl(var(--warning))]" />
              Substituir notas na proposta?
            </DialogTitle>
            <DialogDescription>
              A proposta já tem notas do Consultor. Se aplicares agora,
              o texto anterior é <b className="text-foreground">substituído</b> pelo novo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">
                Texto actual na proposta
              </div>
              <div className="text-xs text-foreground whitespace-pre-wrap max-h-[120px] overflow-y-auto leading-relaxed">
                {((lead?.proposal || {}).additional_notes || "").slice(0, 800)}
              </div>
            </div>
            <div className="rounded-md border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/60 p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--success))] font-semibold mb-1.5">
                Texto que vai substituir
              </div>
              <div className="text-xs text-foreground whitespace-pre-wrap max-h-[120px] overflow-y-auto leading-relaxed">
                {(ai?.client_text || "").slice(0, 800)}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setOverwriteOpen(false)}
              data-testid="rt-notes-overwrite-cancel"
              className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold"
            >
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
            <button
              type="button"
              disabled={applyingClient}
              onClick={async () => {
                setOverwriteOpen(false);
                await applyClientToProposal();
              }}
              data-testid="rt-notes-overwrite-confirm"
              className="inline-flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-primary-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-40"
            >
              {applyingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Substituir
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// QuickZohoNote — one-liner note for the Consultor that skips the whole
// minute + IA flow. Ideal for quick status updates ("cliente pediu para
// chamar amanhã", "confirmado o horário"). Hits ``/leads/{id}/notes``
// which stamps ``author_role=consultor`` and mirrors to Zoho CRM.
// ---------------------------------------------------------------------------
function QuickZohoNote({ leadId, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const content = (text || "").trim();
    if (!content) { toast.error("Nota vazia"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/notes`, { content });
      toast.success("Nota enviada ao Zoho");
      setText("");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar nota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="rt-notes-quick-zoho"
      className="mb-4 flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-3"
    >
      <div className="flex-shrink-0 pt-1.5 text-muted-foreground">
        <FileText className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-1.5">
          Nota rápida ao Zoho <span className="text-muted-foreground normal-case tracking-normal">— ex: "confirmado o horário"</span>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="rt-notes-quick-zoho-input"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escreve uma nota curta que será sincronizada com o Zoho…"
            onKeyDown={(e) => { if (e.key === "Enter" && !saving) save(); }}
            className="flex-1 bg-card border border-border rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:border-primary"
            disabled={saving}
          />
          <button
            type="button"
            data-testid="rt-notes-quick-zoho-save"
            onClick={save}
            disabled={saving || !text.trim()}
            className="inline-flex items-center gap-1.5 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}

